// src/components/resources/tools/generators/FakeChat/FakeChatControls.tsx

'use client'

import { useCallback, useMemo, type MutableRefObject, type RefObject } from 'react'
import { toast } from 'sonner'
import { cn } from '~/lib/utils'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import {
  useFakeChatActions,
  useFakeChatSelectors,
  useFakeChatState
} from '~/components/resources/tools/generators/FakeChat/FakeChatContext'
import type { ChatMessage, ChatPlatform, MessageAlignment, MessageStatus, ParticipantRole } from '~/components/resources/tools/generators/FakeChat/FakeChat.types'
import { useStepScrollReset } from '~/components/resources/tools/generators/shared/useStepScrollReset'
import { captureAndDownload } from '~/components/resources/tools/generators/shared/exportImage'
import { ExportButton } from '~/components/resources/tools/generators/shared/ExportButton'
import { useExport } from '~/components/resources/tools/generators/shared/useExport'
import { waitForStable } from '~/components/resources/tools/generators/shared/waitForStable'
import { AVATAR_SWATCHES } from '~/components/resources/tools/generators/FakeChat/avatarNameColors'

const DEFAULT_TRACKING_SLUG = 'fake-chat-generator'

// Swatch options are derived from the single `AVATAR_SWATCHES` source of truth
// (shared with the WhatsApp/Discord name-colour maps in `avatarNameColors.ts`),
// so adding a swatch in one place flows to the picker and both colour maps —
// and `avatar-name-colors.test.ts` fails the build if a colour entry is missed.
// The human-readable label is derived from the Tailwind token (`bg-indigo-500`
// → `Indigo`).
const avatarSwatches: Array<{ label: string; value: string }> = AVATAR_SWATCHES.map(value => {
  const hue = value.replace(/^bg-/, '').replace(/-\d+$/, '')
  return { value, label: hue.charAt(0).toUpperCase() + hue.slice(1) }
})

const alignmentOptions: Array<{ value: MessageAlignment; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' }
] as const

const statusOptions: Array<{ value: MessageStatus | 'none'; label: string }> = [
  { value: 'none', label: 'Hidden' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'not-delivered', label: 'Not Delivered' }
] as const

const roleOptions: Array<{ value: ParticipantRole; label: string }> = [
  { value: 'member', label: 'Member' },
  { value: 'guest', label: 'Guest' },
  { value: 'bot', label: 'Bot' }
] as const

const platformLabels: Record<ChatPlatform, string> = {
  messaging: 'Modern messaging',
  slack: 'Slack',
  imessage: 'iMessage',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  discord: 'Discord'
} as const

const platformDescriptions: Record<ChatPlatform, string> = {
  messaging: 'Use this for clean, brand-neutral chat transcripts that work across any tool.',
  slack: 'Authentic Slack styling with channel chrome, sidebars, and thread hints.',
  imessage: 'Ideal for testimonial mockups, customer conversations, and high-fidelity phone previews.',
  whatsapp: 'Phone-native WhatsApp styling with the teal header, green bubbles, and delivery ticks.',
  messenger: 'Facebook Messenger styling with blue gradient bubbles, avatars, and an "Active now" header.',
  discord: 'Desktop Discord styling with the server rail, channel sidebar, and grouped message rows.'
} as const

/**
 * Controls panel for the FakeChat component.
 * Provides UI for managing conversation metadata, participants, and messages.
 * 
 * This version presents all controls in a single scrollable panel, grouped by section,
 * so users never have to "tab" to access a single input.
 */
/**
 * Props for FakeChatControls.
 *
 * `previewRef` points at the rendered preview wrapper (owned by the
 * FakeChatGenerator shell) so the Download button can capture the
 * styled mockup with html2canvas.
 */
interface FakeChatControlsProps {
  previewRef: RefObject<HTMLDivElement | null>
  /**
   * Set to `true` by the deferred preview boundary while it trails live state.
   * Read before export so Download can't capture a still-pending frame.
   */
  previewStaleRef?: MutableRefObject<boolean>
  /**
   * Resource slug for export analytics. Defaults to the umbrella
   * `fake-chat-generator`; landing-page variants pass their own slug so
   * per-variant export conversions are attributed to the page the user is
   * actually on.
   */
  trackingSlug?: string
}

export function FakeChatControls({ previewRef, previewStaleRef, trackingSlug = DEFAULT_TRACKING_SLUG }: FakeChatControlsProps): JSX.Element {
  const { conversation, activeMessageId, isExporting } = useFakeChatState()
  const selectors = useFakeChatSelectors()
  const actions = useFakeChatActions()
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(
    () => createEngagementTimers(),
    [],
  )

  const trackInteract = useCallback(
    (interactionType: string, extra?: Record<string, string | number | boolean>): void => {
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: interactionType,
          ...extra,
        },
      })
    },
    [getDeltaEngagementMs, trackingSlug],
  )
  // Reuse the shared export lifecycle for the html2canvas warm-on-mount and the
  // synchronous re-entrancy guard. The VISIBLE busy flag stays the context's
  // `isExporting` (the reducer owns it across the provider tree), so we drive it
  // via `actions.setExporting` rather than the hook's own flag.
  const { runExport } = useExport()

  /**
   * Captures the live preview as a retina-density PNG and triggers a
   * browser download. Mirrors SocialMediaMockupGenerator.handleExport /
   * EmailThreadGenerator.downloadPNG (dynamic html2canvas import →
   * canvas.toBlob → <a download>.png), wired through the existing
   * `setExporting` reducer slot.
   */
  const handleExport = useCallback((): Promise<void> => runExport(async () => {
    if (!previewRef.current) {
      toast.error('Preview not found', { description: 'Please try again in a moment.' })
      return
    }

    // The preview renders from a deferred snapshot; if the user edited the chat
    // or switched platform and hit Download before it committed, wait for it to
    // converge so the captured PNG matches the live conversation/platform.
    if (previewStaleRef) {
      const stabilized = await waitForStable(() => !previewStaleRef.current)
      if (!stabilized) {
        toast.error('Preview is still updating — pause a moment, then export again.')
        return
      }
    }

    // Flip the busy flag only AFTER the gate so it tracks the capture, not the
    // wait, and never shows on the abort path above.
    actions.setExporting(true)
    try {
      await captureAndDownload({
        element: previewRef.current,
        format: 'png',
        filename: `fake-chat-${conversation.metadata.platform}-${Date.now()}`,
        trackingSlug,
        engagementMs: getTotalEngagementMs(),
        // The phone-framed previews scroll their transcript inside a fixed-
        // height frame; expand so long conversations export in full instead of
        // being clipped to the viewport.
        expandScrollableContent: true,
        onSuccess: () =>
          toast.success('Download started', { description: 'Your chat mockup PNG is downloading.' }),
        onError: () =>
          toast.error('Export failed', { description: 'Could not generate image. Please try again.' }),
      })
    } finally {
      actions.setExporting(false)
    }
  }), [runExport, previewRef, previewStaleRef, actions, conversation.metadata.platform, trackingSlug, getTotalEngagementMs])
  // Remove tab state entirely
  // const [activeSection, setActiveSection] = useState<ControlSectionId>('platform')

  // Attach scroll reset logic to the actual scrolling container (the <aside> element)
  // This ensures the scroll behavior works correctly on large screens where the panel overflows
  const scrollContainerRef = useStepScrollReset<HTMLElement>(0, {
    offset: 96
  })

  const activeMessage = selectors.getActiveMessage()

  /**
   * Adds a new participant to the conversation with default values.
   */
  const handleAddParticipant = (): void => {
    const newParticipantId = actions.generateParticipantId()
    actions.addParticipant({
      id: newParticipantId,
      displayName: 'New Teammate',
      handle: '@username',
      avatarInitials: 'NT',
      avatarColor: 'bg-sky-500',
      role: 'member'
    })
  }

  /**
   * Adds a new message to the conversation.
   * Uses the first participant as the sender, or returns early if no participants exist.
   */
  const handleAddMessage = (): void => {
    if (conversation.participants.length === 0) {
      return
    }

    const sender = conversation.participants[0]
    if (!sender) {
      // This should never happen due to the length check above, but TypeScript doesn't know that
      return
    }

    const now = new Date()
    const formatted = now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    })

    const newMessage: ChatMessage = {
      id: actions.generateMessageId(),
      participantId: sender.id,
      text: 'New message…',
      timestamp: formatted,
      alignment: sender.isSelf ? 'right' : 'left',
      reactions: []
    }

    actions.addMessage(newMessage)
    trackInteract('add_message', { message_count: conversation.messages.length + 1 })
  }

  /**
   * Updates a message's status, handling the special 'none' case.
   */
  const handleStatusChange = (messageId: string, value: MessageStatus | 'none'): void => {
    actions.updateMessage(messageId, {
      status: value === 'none' ? undefined : value
    })
  }

  return (
    <aside
      ref={scrollContainerRef}
      className="overflow-hidden rounded-3xl border border-slate-200 bg-white/80 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:max-h-[calc(100dvh-8rem)] lg:overflow-y-auto"
    >
      <div className="flex flex-col px-6 pb-6">
        {/* Export Section */}
        <section aria-labelledby="fake-chat-export" className="space-y-3 pt-6">
          <h2 id="fake-chat-export" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Download a retina-density PNG of your chat mockup, ready for decks, social, and testimonials.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={null}
            className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          />
        </section>

        {/* Platform Section */}
        <section aria-labelledby="fake-chat-platform" className="space-y-4 pt-8">
          <div className="flex items-center justify-between">
            <h2 id="fake-chat-platform" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Platform
            </h2>
            <button
              type="button"
              onClick={() => actions.resetConversation()}
              className="text-xs font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
            >
              Reset preset
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Choose a preset for the interface chrome and initial metadata.
          </p>
          <div className="grid grid-cols-1 gap-2">
            {(Object.keys(platformLabels) as ChatPlatform[]).map(platform => {
              const isActivePlatform = conversation.metadata.platform === platform
              return (
                <button
                  key={platform}
                  type="button"
                  onClick={() => {
                    actions.setPlatform(platform)
                    trackInteract('configuration_change', { platform })
                  }}
                  className={cn(
                    'flex flex-col items-start rounded-2xl border px-4 py-3 text-left transition-all',
                    isActivePlatform
                      ? 'border-indigo-500/70 bg-indigo-50 text-indigo-700 dark:border-indigo-400/60 dark:bg-indigo-500/20 dark:text-indigo-200'
                      : 'border-slate-200 bg-white hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-700/40 dark:hover:text-indigo-300'
                  )}
                >
                  <span className="text-sm font-semibold">{platformLabels[platform]}</span>
                  <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">{platformDescriptions[platform]}</span>
                </button>
              )
            })}
          </div>
        </section>

        {/* Conversation Section */}
        <section aria-labelledby="fake-chat-metadata" className="space-y-4 pt-8">
          <h2 id="fake-chat-metadata" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Conversation details
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Set channel details, layout toggles, and accent colours.
          </p>
          <label className="block space-y-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Workspace</span>
            <input
              type="text"
              value={conversation.metadata.workspaceName}
              onChange={event => actions.updateMetadata({ workspaceName: event.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
              placeholder="Campaign Hub"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Channel or contact</span>
            <input
              type="text"
              value={conversation.metadata.channelName}
              onChange={event => actions.updateMetadata({ channelName: event.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
              placeholder="#campaign-launch"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Topic or subtitle</span>
            <textarea
              value={conversation.metadata.channelTopic}
              onChange={event => actions.updateMetadata({ channelTopic: event.target.value })}
              rows={2}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={conversation.metadata.showAvatars}
                onChange={event => actions.updateMetadata({ showAvatars: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show avatars
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={conversation.metadata.showReactions}
                onChange={event => actions.updateMetadata({ showReactions: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show reactions
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={conversation.metadata.showTimestamps}
                onChange={event => actions.updateMetadata({ showTimestamps: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show timestamps
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={conversation.metadata.typingIndicator}
                onChange={event => actions.updateMetadata({ typingIndicator: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Typing indicator
            </label>
            {conversation.metadata.platform === 'slack' && (
              <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={conversation.metadata.sidebarCollapsed ?? false}
                  onChange={event => actions.updateMetadata({ sidebarCollapsed: event.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Collapse sidebar
              </label>
            )}
          </div>
          <label className="block space-y-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Status footer</span>
            <input
              type="text"
              value={conversation.metadata.lastActivityLabel}
              onChange={event => actions.updateMetadata({ lastActivityLabel: event.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
              placeholder="Active 5 minutes ago"
            />
          </label>
        </section>

        {/* Participants Section */}
        <section aria-labelledby="fake-chat-participants" className="space-y-4 pt-8">
          <div className="flex items-center justify-between">
            <h2 id="fake-chat-participants" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Participants
            </h2>
            <button
              type="button"
              onClick={handleAddParticipant}
              className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50 dark:border-indigo-500/40 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
            >
              Add participant
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Manage the people (or bots) who appear in the conversation.
          </p>
          <div className="space-y-3">
            {conversation.participants.map(participant => (
              <div key={participant.id} className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm transition dark:border-slate-800 dark:bg-slate-900/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={cn('flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-white shadow', participant.avatarColor)}>
                      {participant.avatarInitials}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{participant.displayName}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{participant.handle ?? 'No handle set'}</p>
                    </div>
                  </div>
                  {conversation.participants.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => actions.removeParticipant(participant.id)}
                      className="text-xs font-medium text-rose-500 hover:text-rose-600 dark:text-rose-300"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    Display name
                    <input
                      type="text"
                      value={participant.displayName}
                      onChange={event => actions.updateParticipant(participant.id, { displayName: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                    />
                  </label>
                  <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    Handle
                    <input
                      type="text"
                      value={participant.handle ?? ''}
                      onChange={event => actions.updateParticipant(participant.id, { handle: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                      placeholder="@alex"
                    />
                  </label>
                  <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    Role
                    <select
                      value={participant.role}
                      onChange={event => actions.updateParticipant(participant.id, { role: event.target.value as ParticipantRole })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                    >
                      {roleOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    Avatar color
                    <select
                      value={participant.avatarColor}
                      onChange={event => actions.updateParticipant(participant.id, { avatarColor: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                    >
                      {avatarSwatches.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Messages Section */}
        <section aria-labelledby="fake-chat-messages" className="space-y-4 pt-8">
          <div className="flex items-center justify-between">
            <h2 id="fake-chat-messages" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Messages
            </h2>
            <button
              type="button"
              onClick={handleAddMessage}
              disabled={conversation.participants.length === 0}
              className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-500/40 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
              title={conversation.participants.length === 0 ? 'Add a participant first' : 'Add a new message'}
            >
              Add message
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Draft the transcript, align bubbles, and toggle thread + status details.
          </p>
          <div className="space-y-2">
            {conversation.messages.map(message => {
              const sender = selectors.getParticipantById(message.participantId)
              const isActiveMessage = activeMessageId === message.id
              return (
                <button
                  key={message.id}
                  type="button"
                  onClick={() => actions.setActiveMessage(message.id)}
                  className={cn(
                    'w-full rounded-xl border px-3 py-2 text-left text-sm transition',
                    isActiveMessage
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400/60 dark:bg-indigo-500/10 dark:text-indigo-200'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-indigo-600/40 dark:hover:text-indigo-300'
                  )}
                >
                  <span className="font-semibold">{sender?.displayName ?? 'Unknown'}</span>
                  <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">{message.timestamp}</span>
                  <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 line-clamp-1">{message.text}</span>
                </button>
              )
            })}
          </div>

          {activeMessage ? (
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Editing message</p>
                <button
                  type="button"
                  onClick={() => actions.removeMessage(activeMessage.id)}
                  className="text-xs font-medium text-rose-500 hover:text-rose-600 dark:text-rose-300"
                >
                  Delete message
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                  Sender
                  <select
                    value={activeMessage.participantId}
                    onChange={event => actions.updateMessage(activeMessage.id, { participantId: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                  >
                    {conversation.participants.map(participant => (
                      <option key={participant.id} value={participant.id}>
                        {participant.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                  Alignment
                  <select
                    value={activeMessage.alignment}
                    onChange={event => actions.updateMessage(activeMessage.id, { alignment: event.target.value as MessageAlignment })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                  >
                    {alignmentOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                  Timestamp
                  <input
                    type="text"
                    value={activeMessage.timestamp}
                    onChange={event => actions.updateMessage(activeMessage.id, { timestamp: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                    placeholder="9:45 AM"
                  />
                </label>
                <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                  Delivery status
                  <select
                    value={activeMessage.status ?? 'none'}
                    onChange={event => handleStatusChange(activeMessage.id, event.target.value as MessageStatus | 'none')}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="mt-3 block space-y-2 text-xs text-slate-500 dark:text-slate-400">
                Message text
                <textarea
                  value={activeMessage.text}
                  onChange={event => actions.updateMessage(activeMessage.id, { text: event.target.value })}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                />
              </label>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={Boolean(activeMessage.isThreaded)}
                    onChange={event =>
                      actions.updateMessage(activeMessage.id, {
                        isThreaded: event.target.checked,
                        threadReplyCount: event.target.checked ? activeMessage.threadReplyCount ?? 3 : undefined
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Show thread preview
                </label>
                {activeMessage.isThreaded ? (
                  <label className="block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    Thread reply count
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={activeMessage.threadReplyCount ?? 1}
                      onChange={event =>
                        actions.updateMessage(activeMessage.id, {
                          threadReplyCount: Math.max(0, Number(event.target.value) || 0)
                        })
                      }
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900"
                    />
                  </label>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">Select a message to edit its details.</p>
          )}
        </section>
      </div>
    </aside>
  )
}
